import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ARTICLES, getArticle, formatDate, slugify } from "@/data/articles";
import { getService } from "@/data/services";
import { Seo, breadcrumbSchema } from "@/components/layout/Seo";
import { Breadcrumbs, MaskLines, Reveal } from "@/components/ui/motion";
import { SITE, waLink } from "@/lib/config";

export default function ArticleDetail() {
  const { slug } = useParams();
  const { scrollYProgress } = useScroll();
  const a = getArticle(slug);
  if (!a) return <Navigate to="/artikel" replace />;
  const service = getService(a.service);
  const crumbs = [{ to: "/", label: "Beranda" }, { to: "/artikel", label: "Artikel" }, { to: `/artikel/${a.slug}`, label: a.title }];
  const related = ARTICLES.filter((x) => x.slug !== a.slug && (x.category === a.category || x.service === a.service)).slice(0, 3);
  const articleSchema = {
    "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.excerpt, datePublished: a.date, dateModified: a.date, inLanguage: "id-ID",
    author: { "@type": "Organization", name: "Mika Solution" }, publisher: { "@type": "Organization", name: "Mika Solution", logo: { "@type": "ImageObject", url: `${SITE.url}/images/logo-mika.png` } },
    mainEntityOfPage: `${SITE.url}/artikel/${a.slug}`,
  };

  return (
    <>
      <Seo title={`${a.title} | Mika Solution`} description={a.excerpt} path={`/artikel/${a.slug}`} type="article" schema={[articleSchema, breadcrumbSchema(crumbs)]} />
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed left-0 right-0 top-0 z-[70] h-[3px] origin-left bg-sky-brand" aria-hidden="true" />
      <article data-testid="article-detail">
        <header className="bg-ice">
          <div className="wrap pb-16 pt-10 sm:pb-20 sm:pt-14">
            <Breadcrumbs items={crumbs} />
            <p className="mt-10 text-[12px] font-bold uppercase tracking-[0.18em] text-accentb">{a.category} · {a.readTime} menit baca</p>
            <MaskLines lines={[a.title]} className="mt-5 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl" />
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink md:text-lg">{a.excerpt}</p>
            <p className="mt-6 text-[13px] text-ink-soft">Dipublikasikan {formatDate(a.date)} oleh Tim Mika Solution</p>
          </div>
        </header>
        <div className="wrap grid grid-cols-1 gap-12 py-16 lg:grid-cols-12 lg:py-24">
          <aside className="lg:col-span-3">
            <nav className="lg:sticky lg:top-32" aria-label="Daftar isi" data-testid="article-toc">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-ink-soft">Daftar isi</p>
              <ol className="mt-4 space-y-3 border-l border-line pl-4">
                {a.sections.map((s) => <li key={s.h}><a href={`#${slugify(s.h)}`} className="link-u text-[14px] leading-snug text-slateb">{s.h}</a></li>)}
              </ol>
            </nav>
          </aside>
          <div className="prose-mika max-w-[720px] lg:col-span-7">
            {a.sections.map((s) => (
              <section key={s.h}>
                <h2 id={slugify(s.h)}>{s.h}</h2>
                {s.p.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
                {s.list && <ul>{s.list.map((l) => <li key={l}>{l}</li>)}</ul>}
              </section>
            ))}
            <div className="mt-12 rounded-2xl border border-line p-6">
              <p className="!mb-3 text-[12px] font-bold uppercase tracking-[0.18em] text-ink-soft">Sumber & rujukan</p>
              <ul className="!mb-0">{a.sources.map((s) => <li key={s.label}><a href={s.url} target="_blank" rel="noopener noreferrer" className="link-u text-accentb">{s.label}</a></li>)}</ul>
            </div>
            <p className="!mt-6 text-[13.5px] !leading-relaxed text-ink-soft">Artikel ini bersifat edukasi umum dan bukan nasihat hukum atau keuangan untuk kondisi tertentu.</p>
            <Reveal className="mt-12 rounded-3xl bg-ice p-8 sm:p-10" data-testid="article-cta">
              <p className="font-display text-2xl font-bold text-slateb">Ingin memahami kondisi Anda secara lebih spesifik?</p>
              <p className="!mb-6 !mt-3 !text-[15px]">Konsultasi awal tidak dikenakan biaya. Anda bebas memutuskan setelah memahami opsinya.</p>
              <div className="flex flex-wrap gap-3">
                <a href={waLink(service?.key)} target="_blank" rel="noopener noreferrer" className="btn btn-sky" data-testid="article-wa-cta">Tanya via WhatsApp <ArrowUpRight size={16} /></a>
                {service && <Link to={`/layanan/${service.slug}`} className="btn btn-ghost" data-testid="article-service-link">Layanan {service.name}</Link>}
              </div>
            </Reveal>
          </div>
        </div>
      </article>
      <section className="border-t border-line py-16 sm:py-24" data-testid="related-articles">
        <div className="wrap">
          <p className="eyebrow">Artikel terkait</p>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/artikel/${r.slug}`} className="card group p-7 transition-colors hover:border-sky-brand" data-testid={`related-${r.slug}`}>
                <span className="text-[12px] font-bold uppercase tracking-[0.16em] text-accentb">{r.category}</span>
                <span className="mt-4 block font-display text-lg font-bold leading-snug text-slateb">{r.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
